import { Property } from '@go-shared/types/property';
import { BareSimpleSailingDayBundle, SimpleSailingDayBundle } from '@go-shared/types/sailing';
import React, { ReactNode, useEffect, useState } from 'react';

import { BubbleForm, BubbleOverlay } from '../BubbleUI';
import { BubbleFormPage } from '../BubbleUI/BubbleForm';
import {
  GroupBooking,
  GroupSubgroup,
  VenueRequest,
  VenueRequestFile,
} from '@go-shared/types/groupBooking';
import {
  uploadBookingFile,
  deleteBookingFile,
  uploadGroupFile,
  deleteGroupFile,
  uploadSubgroupFile,
  deleteSubgroupFile,
  getGroupBooking,
  populate,
} from '../../lib/api/groupBooking';
import { VenueTemplate } from '@go-shared/types/venueTemplate';

import './GroupBookingForm.scss';
import {
  BookingForm,
  buildCreateBookingWorkflow,
  buildEditBookingWorkflow,
  buildEditGroupWorkflow,
  buildEditSubgroupWorkflow,
  fetchDataBundle,
  generateDefaultBookingForm,
  generateDefaultGroupForm,
  generateDefaultSubgroupForm,
  GroupBookingDataBundle,
  GroupForm,
  SubgroupForm,
} from './Workflows';
import { DefaultMode, GroupsMode } from '@go-client/pages/GroupsHome';
import { Alert } from '@spark-labs/avocado-ui';
import { safeIdCompare } from '@go-shared/lib/utils';
import { findSailingBundleByDateAndDay } from '@go-shared/lib/sailing';
import { User } from '@go-shared/types/user';
import { BrandGroupType } from '@go-shared/types/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

export interface RequestItinerary {
  sailingBundle: SimpleSailingDayBundle;
  venue: VenueTemplate;
}

interface GroupBookingFormProps {
  user: User;
  property: Property;
  mode: Exclude<GroupsMode, DefaultMode>;
  groupTypes: BrandGroupType[];
  defaultStartDate?: string;
  defaultEndDate?: string;
  data?: GroupBookingDataBundle;
  hideDayContextButton?: boolean;
  onClose?: () => void;
  onSubmitGroupBookings?: () => void;
}

// this component auto-resolves dataBundle if parent does not provide it, hence it can work
// with or without required context from the parent
export default function GroupBookingForm(props: GroupBookingFormProps) {
  const { brand } = useServerInfo();
  const [showDayContext, setShowDayContext] = useState(true);
  const [isNewGroup, setIsNewGroup] = useState(false);
  const [lastSubmissionTimestamp, setLastSubmissionTimestamp] = useState(0);
  const { languages, defaultLanguage } = useServerInfo();

  const [dataBundle, setDataBundle] = useState<GroupBookingDataBundle | undefined>(undefined);

  const [selectedGroup, setSelectedGroup] = useState<GroupBooking | undefined>(undefined);
  const [selectedBooking, setSelectedBooking] = useState<VenueRequest | undefined>(undefined);
  const [selectedSubgroup, setSelectedSubgroup] = useState<GroupSubgroup | undefined>(undefined);

  const [selectedVenue, setSelectedVenue] = useState<VenueTemplate | undefined>(undefined);
  const [selectedSailingBundle, setSelectedSailingBundle] = useState<
    BareSimpleSailingDayBundle | undefined
  >(undefined);

  const [groupForm, setGroupForm] = useState<GroupForm>(generateDefaultGroupForm());
  const [bookingForm, setBookingForm] = useState<BookingForm>(generateDefaultBookingForm());
  const [subgroupForm, setSubgroupForm] = useState<SubgroupForm>(generateDefaultSubgroupForm());

  useEffect(() => {
    setGroupForm(generateDefaultGroupForm(selectedGroup));
  }, [selectedGroup]);

  useEffect(() => {
    setBookingForm(generateDefaultBookingForm(selectedBooking));
  }, [selectedBooking]);

  useEffect(() => {
    setSubgroupForm(generateDefaultSubgroupForm(selectedSubgroup));
  }, [selectedSubgroup]);

  useEffect(() => {
    initialize().catch(console.error);
  }, [props.data]);

  async function initialize() {
    const bundle = props.data || (await fetchDataBundle(props.property));

    let groupInData: GroupBooking | undefined;
    let subgroupInData: GroupSubgroup | undefined;
    let bookingInData: VenueRequest | undefined;
    let sailingBundle: BareSimpleSailingDayBundle | undefined;
    let venue: VenueTemplate | undefined;

    const groupMatch = bundle.simpleGroupBookings.find(gbk =>
      safeIdCompare(gbk._id, selectedGroup?._id || props.mode.groupId)
    );

    if (groupMatch) {
      const bareGroupBooking = await getGroupBooking(groupMatch._id);

      groupInData = populate(
        bareGroupBooking,
        props.property,
        bundle.allUsers,
        bundle.venueTemplates,
        bundle.groupResources
      );
    }

    switch (props.mode.type) {
      case 'edit':
      case 'create': {
        const { bookingId } = props.mode;

        bookingInData = groupInData?.requests.find(rq =>
          safeIdCompare(rq._id, selectedBooking?._id || bookingId)
        );

        if (bookingInData) {
          sailingBundle = findSailingBundleByDateAndDay(
            bookingInData.date,
            bookingInData.dayIndex,
            bundle.simpleSailings
          );
          venue = bookingInData.venue;
        }
        break;
      }
      case 'subgroup-edit': {
        const { subgroupId } = props.mode;

        subgroupInData = groupInData?.subgroups.find(sbg =>
          safeIdCompare(sbg._id, selectedSubgroup?._id || subgroupId)
        );
        break;
      }
    }

    if (groupInData) setSelectedGroup(groupInData);
    if (subgroupInData) setSelectedSubgroup(subgroupInData);
    if (bookingInData) setSelectedBooking(bookingInData);
    if (sailingBundle) setSelectedSailingBundle(sailingBundle);
    if (venue) setSelectedVenue(venue);

    setDataBundle(bundle);
  }

  async function handleOnSubmitGroupBooking() {
    if (props.onSubmitGroupBookings) props.onSubmitGroupBookings();

    // reinitialize if parent is not providing info
    if (!props.data) await initialize();

    setLastSubmissionTimestamp(Date.now());
  }

  function handleSelectGroup(group: GroupBooking) {
    setSelectedGroup(group);
  }

  function handleSelectSailingBundle(bundle: BareSimpleSailingDayBundle) {
    setSelectedSailingBundle(bundle);
  }

  function handleSelectVenue(venue: VenueTemplate) {
    setSelectedVenue(venue);
  }

  function changeBookingFormAttribute<T extends keyof BookingForm>(key: T, val: BookingForm[T]) {
    setBookingForm(prev => ({
      ...prev,
      _dirty: true,
      [key]: val,
    }));
  }

  async function handleBookingUploadFile(file: File) {
    const result = await uploadBookingFile(
      file,
      isNewGroup ? undefined : selectedGroup?._id,
      selectedBooking?._id
    );

    changeBookingFormAttribute('files', [...bookingForm.files, result]);
  }

  async function handleBookingUpdateFile(file: VenueRequestFile) {
    const newFiles = [...bookingForm.files];

    newFiles.splice(
      newFiles.findIndex(f => f._id === file._id),
      1,
      file
    );

    changeBookingFormAttribute('files', newFiles);
  }

  async function handleBookingDeleteFile(file: VenueRequestFile) {
    let shouldContinue = await Alert.question(
      `Are you sure you want to remove the file [${file.name}] from this venue request?`
    );

    if (!shouldContinue) return;

    if (!isNewGroup && selectedGroup && selectedBooking) {
      await deleteBookingFile(selectedGroup._id, selectedBooking._id, file._id);
    }

    changeBookingFormAttribute(
      'files',
      bookingForm.files.filter(f => !safeIdCompare(f._id, file._id))
    );
  }

  function changeGroupFormAttribute<T extends keyof GroupForm>(key: T, val: GroupForm[T]) {
    setGroupForm({ ...groupForm, _dirty: true, [key]: val });
  }

  async function handleGroupUploadFile(file: File) {
    const result = await uploadGroupFile(file, isNewGroup ? undefined : selectedGroup?._id);

    changeGroupFormAttribute('files', [...groupForm.files, result]);
  }

  async function handleGroupUpdateFile(file: VenueRequestFile) {
    const newFiles = [...groupForm.files];

    newFiles.splice(
      newFiles.findIndex(f => f._id === file._id),
      1,
      file
    );

    changeGroupFormAttribute('files', newFiles);
  }

  async function handleGroupDeleteFile(file: VenueRequestFile) {
    let shouldContinue = await Alert.question(
      `Are you sure you want to remove the file [${file.name}] from this group?`
    );

    if (!shouldContinue) return;

    if (!isNewGroup && selectedGroup) {
      await deleteGroupFile(file._id, selectedGroup._id);
    }

    changeGroupFormAttribute(
      'files',
      groupForm.files.filter(f => !safeIdCompare(f._id, file._id))
    );
  }

  function changeSubgroupFormAttribute<T extends keyof SubgroupForm>(key: T, val: SubgroupForm[T]) {
    setSubgroupForm({ ...subgroupForm, _dirty: true, [key]: val });
  }

  async function handleSubgroupUploadFile(file: File) {
    const result = await uploadSubgroupFile(file, selectedGroup?._id, selectedSubgroup?._id);

    changeSubgroupFormAttribute('files', [...subgroupForm.files, result]);
  }

  async function handleSubgroupUpdateFile(file: VenueRequestFile) {
    const newFiles = [...subgroupForm.files];

    newFiles.splice(
      newFiles.findIndex(f => f._id === file._id),
      1,
      file
    );

    changeSubgroupFormAttribute('files', newFiles);
  }

  async function handleSubgroupDeleteFile(file: VenueRequestFile) {
    let shouldContinue = await Alert.question(
      `Are you sure you want to remove the file [${file.name}] from this subgroup?`
    );

    if (!shouldContinue) return;

    if (selectedGroup && selectedSubgroup) {
      await deleteSubgroupFile(file._id, selectedGroup._id, selectedSubgroup._id);
    }

    changeSubgroupFormAttribute(
      'files',
      subgroupForm.files.filter(f => !safeIdCompare(f._id, file._id))
    );
  }

  function clearGroupForm() {
    setGroupForm(generateDefaultGroupForm());
  }

  function clearBookingForm() {
    setBookingForm(generateDefaultBookingForm());
  }

  let prependNode: ReactNode | undefined;
  let startAtPage: number | undefined;
  const pages: Array<BubbleFormPage<GroupBookingDataBundle>> = [];

  switch (props.mode.type) {
    case 'edit':
      if (dataBundle && selectedGroup && selectedBooking && selectedVenue) {
        const workflow = buildEditBookingWorkflow(pages, {
          dataBundle,
          selectedGroup,
          selectedBooking,
          selectedVenue,
          selectedSailingBundle,
          bookingForm,
          setBookingForm,
          changeBookingFormAttribute,
          property: props.property,
          user: props.user,
          isNewGroup,
          groupForm,
          showDayContext,
          setShowDayContext,
          languages: languages.map(l => l.key),
          defaultLanguage: defaultLanguage.key,
          handleBookingUploadFile,
          handleBookingUpdateFile,
          handleBookingDeleteFile,
          defaultStartDate: props.defaultStartDate,
          defaultEndDate: props.defaultEndDate,
          onClose: props.onClose,
          handleSelectVenue,
          handleSelectSailingBundle,
          onSubmitBooking: handleOnSubmitGroupBooking,
        });
        prependNode = workflow.prependNode;
        startAtPage = 1;
      }
      break;

    case 'group-edit':
      buildEditGroupWorkflow(pages, {
        selectedGroup,
        groupTypes: brand.groupTypes,
        changeGroupFormAttribute,
        groupForm,
        setGroupForm,
        property: props.property,
        handleGroupUploadFile,
        handleGroupUpdateFile,
        handleGroupDeleteFile,
        onClose: props.onClose,
        onSubmitBooking: handleOnSubmitGroupBooking,
      });
      break;

    case 'subgroup-edit':
      if (selectedGroup && selectedSubgroup) {
        buildEditSubgroupWorkflow(pages, {
          selectedGroup,
          selectedSubgroup,
          changeSubgroupFormAttribute,
          subgroupForm,
          setSubgroupForm,
          property: props.property,
          handleSubgroupUploadFile,
          handleSubgroupUpdateFile,
          handleSubgroupDeleteFile,
          onClose: props.onClose,
          onSubmitBooking: handleOnSubmitGroupBooking,
        });
      }
      break;

    case 'create':
      if (dataBundle) {
        const workflow = buildCreateBookingWorkflow(pages, {
          dataBundle,
          selectedGroup,
          selectedVenue,
          selectedSailingBundle,
          bookingForm,
          setBookingForm,
          clearBookingForm,
          changeBookingFormAttribute,
          groupForm,
          setGroupForm,
          clearGroupForm,
          changeGroupFormAttribute,
          property: props.property,
          user: props.user,
          groupTypes: brand.groupTypes,
          isNewGroup,
          setIsNewGroup,
          showDayContext,
          setShowDayContext,
          lastSubmissionTimestamp,
          defaultStartDate: props.defaultStartDate,
          defaultEndDate: props.defaultEndDate,
          languages: languages.map(l => l.key),
          defaultLanguage: defaultLanguage.key,
          handleGroupUploadFile,
          handleGroupUpdateFile,
          handleGroupDeleteFile,
          handleBookingUploadFile,
          handleBookingUpdateFile,
          handleBookingDeleteFile,
          onClose: props.onClose,
          handleSelectGroup,
          handleSelectVenue,
          handleSelectSailingBundle,
          onSubmitBooking: handleOnSubmitGroupBooking,
        });
        prependNode = workflow.prependNode;
        if (props.mode.groupId) startAtPage = 1;
      }
  }

  return (
    <div className="group-booking-form">
      {prependNode}
      <BubbleOverlay loading={!dataBundle} hideBg loadingMinHeight="large">
        {dataBundle && (
          <BubbleForm resources={dataBundle} pages={pages} startAtPage={startAtPage} />
        )}
      </BubbleOverlay>
    </div>
  );
}
